<template>
<NuxtImg v-if="featuredImgUrl" class="feature-blur-bg" :src="featuredImgUrl" :alt="post?.title || ''" />
<div class="blog-post-container">
    <NuxtLink
        v-slot:default="{navigate}"
        :to="localeRoute('/blog')"
        class="blog-post-link"
    >
        <div class="blog-breadcrumb" aria-hidden v-on:click="navigate">
            <NuxtImg
                class="fa fa-regular fa-circle-left"
                :alt="$t('NAVBAR.BACK')"
                aria-hidden
                focusable="false"
            />
            <span v-t="'BLOG.BACK_TO_BLOG'" class="back-text" />
        </div>
    </NuxtLink>
    <div class="blog-post">
        <NuxtImg
            v-if="featuredImgUrl"
            :src="`${featuredImgUrl}?format=auto`"
            :alt="post?.title || ''"
            class="featured"
        />
        <div class="blog-post-content padded">
            <div class="blog-post-date">
                <NuxtImg
                    class="fa fa-regular fa-calendar-days"
                    alt="date posted"
                    aria-hidden
                    focusable="false"
                />
                {{ datePosted }} |
                <NuxtImg
                    class="fa fa-regular fa-circle-user"
                    alt="author"
                    aria-hidden
                    focusable="false"
                />
                {{ postAuthor }}
            </div>
            <h1>{{ post?.title }}</h1>
            <!-- eslint-disable-next-line vue/no-v-html -->
            <div class="blog-post-body paragraph" v-html="processedContent" />

            <EmailSignup
                v-if="post?.mailingList"
                :subscriber-id="post?.mailingList"
                :data-fields="[{ name: 'language', id: '4242', value: listrakLocale }]"
                :button-text="$t('FORM.SUBMIT')"
            />

            <div class="blog-post-tags">
                <NuxtLink v-for="t in post?.tags?.data" :key="t.attributes.slug" :to="localeRoute(`/blog?tag=${t.attributes.slug}`)">
                    <div class="tag">{{ t.attributes.tag }}</div>
                </NuxtLink>
            </div>
        </div>
    </div>
</div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { I18n } from '$services/i18n'
import { previousLocale } from '~/plugins/i18n/plugin'
import { oembed, loadTweets } from '$services/embed'
import EmailSignup from '$components/forms/EmailSignup.vue'
import type { JBGWeb } from '$types/JBGWeb'

const i18n = useI18n()
const app = useNuxtApp()
const route = useRoute()
const localeRoute = useLocaleRoute()

const postSlug = route.params.postslug as string

const { data: post } = await useAsyncData<JBGWeb.BlogPost>(
    `blog-post-${postSlug}-${i18n.locale.value}-content`,
    async () => {
        try {
            const post = await app.$cms.fetchOne<JBGWeb.BlogPost>('blog-posts', postSlug, {
                populate: ['featuredImage', 'tags', 'author', 'seo', 'seo.metaImage'],
                locale: i18n.locale.value
            })
            return post
        } catch (error) {
            if (error.message === 'No data') {
                try {
                    const post = await app.$cms.fetchOne<JBGWeb.BlogPost>('blog-posts', postSlug, {
                        populate: ['localizations'],
                        locale: previousLocale.value
                    })
                    const hasPublishedLocalization = post.localizations.data.some((x) => {
                        if (x.attributes.locale === I18n.getLocaleForStrapi(i18n.locale.value)) {
                            void navigateTo(x.attributes.slug, { replace: true })
                            return true
                        }
                        return false
                    })
                    if (!hasPublishedLocalization) {
                        void navigateTo(localeRoute('/blog'))
                    }
                } catch (error) {
                    console.error('error loading blog posts', error)
                }
            }
            throw error
        }
    }
)

const { data: processedContent } = await useAsyncData<string>(
    `blog-post-${postSlug}-${i18n.locale.value}-processed-content`,
    async () => oembed(post.value.content)
)

const listrakLocale = computed(() => I18n.getLocaleForListrak(i18n.locale.value))

const datePosted = computed(() => {
    if (!post.value) return ''
    const d = new Date(post.value.publishedAt)
    return d.toLocaleDateString(i18n.locale.value, { year: 'numeric', month: 'long', day: 'numeric' })
})

const postAuthor = computed(() => {
    if (!post.value || !post.value.author) return 'Jackbox Games'
    return post.value.author.data?.attributes.name
})

const featuredImgUrl = computed(() => {
    if (!post.value) {
        return undefined
    }
    const attributes = post.value?.featuredImage?.data?.attributes
    if (attributes?.formats?.large?.url) {
        return attributes.formats.large.url
    }
    return attributes?.url
})

useHead({
    title: `${post.value?.title}`,
    meta: [
        { name: 'description', content: post.value?.seo?.metaDescription },
        { name: 'og:title', content: post.value?.title },
        { name: 'og:description', content: post.value?.seo?.metaDescription },
        { name: 'og:image', content: featuredImgUrl }
    ]
})

if (import.meta.client) {
    void loadTweets()
}

</script>

<style lang="scss">
@use "$styles/kit.scss" as *;

.feature-blur-bg {
    position: fixed;
    z-index: 0;
    width: 100%;
    filter: blur(8px);
    opacity: 0.3;
    @include mq-medium-and-more {
            margin-top: -120px;
    }
    @include mq-small-and-less {
        margin-top: -75px;
    }
}
.blog-post-container {
    position: relative;
    z-index: 1;
    margin-left: auto;
    margin-right: auto;

    @include mq-large {
        max-width: 950px;
    }
    @include mq-medium {
        max-width: 850px;
    }

    .blog-breadcrumb {
        color: var(--neutral-100);
        margin-bottom: 10px;
        .back-text {
            padding-left: 5px;
        }
        &:hover {
            color: var(--primary-200);
        }
    }

    .blog-post {
        color: var(--surface-100);
        background: var(--neutral-100);
        border-radius: 20px;
        padding-bottom: 20px;
        margin-bottom: 25px;

        h2 {
            font-size: 1.3em;
            line-height: 1.3em;
        }

        .tag {
            background-color: var(--neutral-500);
            color: var(--neutral-100);
        }

        .oembed-video {
            width: 100%;
            height: 100%;
            aspect-ratio: 16/9;

            iframe {
                width: 100%;
                height: 100%;
                aspect-ratio: 16/9;
            }
        }

        img.featured {
            border-radius: 15px 15px 0 0;
        }

        .blog-post-content {
            .blog-post-date {
                text-align:center;
                padding-top: 15px;
                font-size: 14px;
            }
            padding-bottom: 50px;
            .blog-post-body {
                a {
                    color: var(--primary-500)
                }
                a:hover {
                    color: var(--primary-700)
                }
                h1: {
                    color: #ff0000;
                    text-transform: none;
                }
                img {
                    max-width: 100%;
                    height: auto;
                }
                figure {
                    margin: 0 auto;
                    margin-block-start: 1em;
                    margin-block-end: 1em;
                    &.image-style-side {
                        float: right;
                    }
                    figcaption {
                        text-align: center;
                        font-size: 12px;
                        font-style:italic;
                        em {
                            font-style:normal;
                        }
                    }
                }
                .embed-youtube {
                    position: relative;
                    padding-bottom: 56.25%; /* 16:9 */
                    height: 0;

                    iframe {
                        position: absolute;
                        top: 0;
                        left: 0;
                        width: 100%;
                        height: 100%;
                    }
                }

                .wp-block-buttons {
                    text-align: center;
                }

                .wp-block-button {
                    border-radius: 4px;
                    border: none;
                    color: var(--neutral-50);
                    background-color: var(--primary-700);
                    display: inline-block;
                    padding: 10px;
                    cursor: pointer;

                    &:disabled {
                        border-color: var(--neutral-400);
                        color: var(--neutral-400);
                    }
                    &:hover:not(:disabled) {
                        background-color: var(--primary-500);
                    }
                    a {
                        color: unset;
                    }
                }

                .wp-block-image {
                    text-align: center;
                    img {
                        max-width: 100%;
                    }
                }
            }

            a.anchor-button {
                color: var(--neutral-100);
                background-color: var(--neutral-600);

                &:disabled {
                    background-color: var(--neutral-700);
                    border-color: var(--neutral-700);
                    color: var(--neutral-200);
                }

                &:hover:not(:disabled) {
                    background-color: var(--neutral-500);
                    color: var(--neutral-50);
                }

                &.primary {
                    background-color: var(--primary-600);

                    &:hover:not(:disabled) {
                        background-color: var(--primary-500);
                        color: var(--neutral-50);
                    }
                }

                &.outline {
                    background-color: transparent;
                    border-color: var(--neutral-600);
                    color: var(--neutral-600);
                    &:hover:not(:disabled) {
                        background-color: transparent;
                        color: var(--neutral-600);
                    }
                }
            }

            a.btn {
                display: block;
                margin: 0 auto;
                margin-block-end: 1em;
                margin-block-start: 1em;
                width: fit-content;
                border: none;
                color: var(--neutral-100);
                background-color: var(--neutral-600);

                @include button;

                &:disabled {
                    border-color: var(--neutral-500);
                    color: var(--neutral-50);
                }

                &:hover:not(:disabled) {
                    background-color: var(--neutral-700);
                    color: var(--neutral-50);
                }
            }

            a.btn.primary {
                background-color: var(--primary-600);
                &:hover:not(:disabled) {
                    background-color: var(--primary-700);
                }
            }

            a.btn.alt {
                background-color: var(--alt-blue-600);
                &:hover:not(:disabled) {
                    background-color: var(--alt-blue-700);
                }
            }

            a.btn.green {
                background-color: var(--green-600);
                &:hover:not(:disabled) {
                    background-color: var(--green-700);
                }
            }

            a.btn.wide {
                width: auto;
            }

            audio {
                display: block;
                margin: 0 auto;
                margin-block-end: 1em;
                margin-block-start: 1em;
            }
        }
    }
    .featured {
        width: 100%;
    }
}


</style>
